/**
 * Warranty Timeline — mobile drag navigation (transform-based).
 *
 * WHY TRANSFORM INSTEAD OF SCROLLLEFT:
 * The earlier `overflow-x: auto` + `scrollLeft` approach silently failed
 * in production — writes to `slider.scrollLeft` were accepted (no error)
 * but readback always returned 0, while `scrollWidth - clientWidth` was a
 * healthy 748px. That's the classic symptom of an ancestor cascade rule
 * resetting overflow to `visible` at computed time, turning the element
 * from a real scroll container into one that just clips/bleeds content
 * visually. Rather than hunt the offending rule across theme + section
 * + base stylesheets, we bypass scroll entirely and translate the rail
 * with CSS `transform: translateX(-offset)`. Transform writes are always
 * honoured regardless of `overflow`, specificity, or hot-reload state.
 *
 * CSS OBLIGATIONS (see warranty-timeline.liquid at <=749px):
 *   .warranty-timeline__slider  { overflow: hidden; position: relative; }
 *   .warranty-timeline__rail    { transform-origin: 0 0; will-change: transform; }
 *
 * BEHAVIOUR:
 *   - Viewport >= 750px: no listeners attached, no transforms applied.
 *   - Viewport < 750px: press + drag anywhere on the slider pans the rail.
 *     Offset is clamped to [0, rail.scrollWidth - slider.clientWidth].
 *   - `data-wt-drag-init` flag prevents double-binding across
 *     `shopify:section:load` re-fires (theme editor / hot reload).
 *   - A capture-phase click handler swallows synthetic taps after a drag
 *     crossed the 3px threshold so caption text doesn't accept clicks
 *     while the user was dragging.
 */
(function () {
  const SECTION_SELECTOR = '[data-warranty-timeline]';
  const SLIDER_SELECTOR = '.warranty-timeline__slider';
  const RAIL_SELECTOR = '.warranty-timeline__rail';
  const INIT_FLAG = 'wtDragInit';
  const DRAG_THRESHOLD_PX = 3;

  function bindSlider(section) {
    const slider = section.querySelector(SLIDER_SELECTOR);
    const rail = section.querySelector(RAIL_SELECTOR);
    const sectionId = section.dataset.sectionId || '(unknown)';

    if (!slider || !rail) {
      console.warn('[warranty-timeline] slider/rail missing', {
        section_id: sectionId,
        has_slider: !!slider,
        has_rail: !!rail,
      });
      return;
    }
    if (slider.dataset[INIT_FLAG] === 'true') return;
    slider.dataset[INIT_FLAG] = 'true';

    const mediaQuery = window.matchMedia('(max-width: 749px)');

    let offset = 0;
    let startOffset = 0;
    let startX = 0;
    let isDragging = false;
    let hasMoved = false;

    const getMaxOffset = () => Math.max(0, rail.scrollWidth - slider.clientWidth);

    const applyTransform = () => {
      rail.style.transform = 'translate3d(' + -offset + 'px, 0, 0)';
    };

    const resetTransform = () => {
      rail.style.transform = '';
      offset = 0;
    };

    /* If viewport crosses the mobile boundary, clear any translate so the
       native desktop layout isn't left with a lingering -300px offset. */
    const handleMediaChange = () => {
      if (!mediaQuery.matches) resetTransform();
    };
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleMediaChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleMediaChange);
    }

    console.log('[warranty-timeline] drag handler attached', {
      section_id: sectionId,
      is_mobile_viewport: mediaQuery.matches,
      rail_scroll_width: rail.scrollWidth,
      slider_client_width: slider.clientWidth,
      max_offset: getMaxOffset(),
    });

    const start = (clientX) => {
      if (!mediaQuery.matches) return false;
      isDragging = true;
      hasMoved = false;
      startX = clientX;
      startOffset = offset;
      slider.style.cursor = 'grabbing';
      slider.style.userSelect = 'none';
      return true;
    };

    const move = (clientX, event) => {
      if (!isDragging) return;
      const dx = clientX - startX;
      if (!hasMoved && Math.abs(dx) > DRAG_THRESHOLD_PX) hasMoved = true;
      const max = getMaxOffset();
      const next = startOffset - dx;
      offset = next < 0 ? 0 : next > max ? max : next;
      applyTransform();
      if (hasMoved && event && event.cancelable) {
        event.preventDefault();
      }
    };

    const end = () => {
      if (!isDragging) return;
      isDragging = false;
      slider.style.cursor = '';
      slider.style.userSelect = '';
    };

    slider.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (!start(e.clientX)) return;
      try { slider.setPointerCapture(e.pointerId); } catch (_) {}
    });
    slider.addEventListener('pointermove', (e) => move(e.clientX, e));
    slider.addEventListener('pointerup', end);
    slider.addEventListener('pointercancel', end);
    slider.addEventListener('pointerleave', end);

    slider.addEventListener('mousedown', (e) => {
      if (e.button !== 0 || isDragging) return;
      start(e.clientX);
    });
    window.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      move(e.clientX, e);
    });
    window.addEventListener('mouseup', end);

    slider.addEventListener('touchstart', (e) => {
      if (!e.touches || !e.touches[0] || isDragging) return;
      start(e.touches[0].clientX);
    }, { passive: true });
    slider.addEventListener('touchmove', (e) => {
      if (!isDragging || !e.touches || !e.touches[0]) return;
      move(e.touches[0].clientX, e);
    }, { passive: false });
    slider.addEventListener('touchend', end);
    slider.addEventListener('touchcancel', end);

    slider.addEventListener('click', (event) => {
      if (hasMoved) {
        event.preventDefault();
        event.stopPropagation();
        hasMoved = false;
      }
    }, true);
  }

  function initAll(root) {
    const scope = root || document;
    const sections = scope.querySelectorAll(SECTION_SELECTOR);
    sections.forEach((section) => bindSlider(section));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event && event.target) initAll(event.target);
  });
})();
